/**
 * The one place a session is ended.
 *
 * There used to be four different wipes — `api.ts`'s expiry handler,
 * `auth.tsx`'s `logout()`, `auth.tsx`'s `failToLogin()`, and the header's
 * `handleLogout()` — and only the header's cleared `user_role`. That mattered:
 * `user_role` is what the client portal reads to decide whether to render the
 * client-admin surface (`app/(client)/layout.tsx`, `portal/page.tsx`), and it is
 * only ever WRITTEN at login. On a shared machine, a session that ended through
 * any of the other three paths left the previous user's role behind for the next
 * person to sign in with.
 *
 * So: one key list, one clear, one logout. Adding a session key means adding it
 * to `SESSION_STORAGE_KEYS` and nowhere else.
 */

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8001";

/** Every localStorage key that belongs to a signed-in session. */
export const SESSION_STORAGE_KEYS = [
  "access_token",
  "refresh_token",
  "user_name",
  "user_email",
  "user_role",
  "user_locale",
] as const;

/** How long to wait for the revocation call before giving up on it. */
const LOGOUT_TIMEOUT_MS = 2500;

/** Drop every session key from localStorage. Safe to call on the server (no-op). */
export function clearSessionStorage(): void {
  if (typeof window === "undefined") return;
  for (const key of SESSION_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }
}

/**
 * Ask the backend to revoke the stored refresh token.
 *
 * Best-effort and deliberately silent: it never throws and never blocks past
 * `LOGOUT_TIMEOUT_MS`. Signing out has to work on a plane, against a backend
 * that is down, and with a token the server has already forgotten — the local
 * clear is what the user is actually asking for, and it must not be hostage to
 * a network call. The endpoint is unauthenticated and idempotent, so a token
 * that is expired, malformed or absent simply does nothing.
 */
async function revokeRefreshToken(): Promise<void> {
  if (typeof window === "undefined") return;
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) return;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOGOUT_TIMEOUT_MS);
  try {
    await fetch(`${API_URL}/auth/logout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
      signal: controller.signal,
      keepalive: true,
    });
  } catch {
    // Offline, aborted, backend down — the local clear below still happens.
  } finally {
    clearTimeout(timer);
  }
}

/**
 * End the session: revoke server-side, then clear locally, then (optionally)
 * navigate.
 *
 * The order matters — revoke first, because the refresh token has to still be
 * in localStorage to be sent. The clear is in a `finally`-equivalent position:
 * whatever the network did, the browser ends up signed out.
 *
 * `redirectTo` uses a full page assignment rather than the Next router so that
 * every provider, context and cached query belonging to the old session is torn
 * down. A soft navigation would leave the previous user's React state in memory.
 */
export interface EndSessionOptions {
  /**
   * Whether to ask the backend to revoke the refresh token first.
   *
   * `true` for a sign-out the user actually asked for — that is the whole point
   * of the endpoint. `false` for the silent-expiry path, where a refresh has
   * JUST failed: the token is either already rejected or the backend is
   * unreachable, so the call is at best a no-op and at worst makes the user
   * stare at a dead page for the abort timeout in exactly the situation where
   * the network is the problem. Expiry is not a revocation event.
   */
  revoke?: boolean;
  /** Where to send the browser afterwards. `null` to stay put. */
  redirectTo?: string | null;
}

export async function endSession({
  revoke = true,
  redirectTo = "/login",
}: EndSessionOptions = {}): Promise<void> {
  if (revoke) {
    await revokeRefreshToken();
  }
  clearSessionStorage();
  if (redirectTo && typeof window !== "undefined") {
    window.location.assign(redirectTo);
  }
}
